/**
 * MainLayout Component
 * Shared layout with navigation for all authenticated pages
 */

import {
  FocusEvent as ReactFocusEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useCurrency } from '../../contexts/CurrencyContext'
import { CURRENCIES } from '../currency/CurrencySelector'
import { NotificationBell } from '../notifications/NotificationBell'
import { RateLimitBanner } from '../banners/RateLimitBanner'
import { GlobalCardSearch } from '../GlobalCardSearch'
import { ActionConfirmModal } from '../modals'
import { useNotificationsFeed } from '../../hooks/useNotifications'
import { api } from '../../utils/api'
import { createRootVarWriter, frameThrottle } from '../../utils/rootCssVars'
import { bottomNavOffsetCss } from '../../utils/bottomNavOffset'
import { GlobalSearchSheet } from '../GlobalCardSearch/GlobalSearchSheet'
import { ErrorBoundary } from './ErrorBoundary'
import { localStorageKeys } from '../../constants/storage'
import styles from './MainLayout.module.css'
import { 
  Home, 
  ClipboardList, 
  BarChart3, 
  QrCode, 
  BookOpen, 
  Settings,
  Database,
  LogOut,
  ArrowLeft,
  LifeBuoy,
  Shield,
  Tag,
  ArrowLeftRight,
  FlaskConical,
  ScanLine,
  Ruler,
  Megaphone,
  Plus,
  PackagePlus,
  FileSpreadsheet,
  ChevronRight,
  Lock,
  Gauge,
  CalendarDays,
  Sun,
  Moon,
  Gavel,
  Menu,
  Search,
  ChevronDown,
  Wrench,
  Check,
  Maximize2,
  Handshake,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon
} from 'lucide-react'

import { useSubscription } from '../../hooks/useSubscription'
import { useOverLimit } from '../../hooks/useOverLimit'
import { overLimitMessage } from '../banners/overLimitMessage'
import { env } from '../../config/env'

interface MainLayoutProps {
  children: ReactNode
}

/**
 * Desktop sidebar widths. Pinned, the sidebar takes its full width and the page moves over
 * for it; folded, it is the icon rail and groups open as a pop-out menu beside it. Either
 * way it never covers the page. Must match `--rail-collapsed` / `--sidebar-pinned` in CSS.
 */
const SIDEBAR_RAIL_WIDTH = 72
const SIDEBAR_PINNED_WIDTH = 280
/** With no saved choice, screens at least this wide start pinned; narrower ones (iPads) start folded. */
const PIN_BY_DEFAULT_MIN_WIDTH = 1280

const readSidebarPinned = (): boolean => {
  try {
    const stored = window.localStorage.getItem(localStorageKeys.sidebarPinned)
    if (stored === '1') return true
    if (stored === '0') return false
  } catch {
    // Storage blocked: fall back to the width default.
  }
  return typeof window !== 'undefined' && window.innerWidth >= PIN_BY_DEFAULT_MIN_WIDTH
}

interface NavSubItem {
  path: string
  icon: LucideIcon
  label: string
  exact?: boolean
  locked?: boolean
  requiredPlanLabel?: string
  roleRestricted?: boolean
  restrictedLabel?: string
}

interface NavItem {
  path: string
  icon: LucideIcon
  label: string
  exact?: boolean
  locked?: boolean
  requiredPlanLabel?: string
  submenu?: NavSubItem[]
}

/** A locked item's lock and the plan that unlocks it, kept together so they wrap onto one line. */
const PlanLock = ({ plan, size }: { plan?: string; size: number }) => (
  <span className={styles.navLockGroup} title={plan ? `Requires ${plan}` : undefined}>
    <Lock size={size} className={styles.navLock} />
    {plan && <span className={styles.navLockText}>{plan}</span>}
  </span>
)

export const MainLayout = ({ children }: MainLayoutProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, authWarning, dismissWarning } = useAuth()
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const { unreadTicketCount } = useNotificationsFeed()
  const [hasEbayIntegration, setHasEbayIntegration] = useState<boolean>(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false)
  const [isSidebarPinned, setIsSidebarPinned] = useState(readSidebarPinned)
  // Folded rail only: the group whose pop-out menu is open, and the label shown beside a hovered icon.
  const [railFlyout, setRailFlyout] = useState<{ path: string; top: number } | null>(null)
  const [railTip, setRailTip] = useState<{ label: string; top: number } | null>(null)
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(max-width: 768px)').matches
  })
  const { canAccessQrCodes, canBulkOps, canAccessTradeCenter, canAccessCalendar: canAccessCalendarTier, can, tierName, requiredTierFor } = useSubscription()
  const overLimitWarning = overLimitMessage(useOverLimit())
  const { theme, toggleTheme } = useTheme()
  const { currency, setCurrency } = useCurrency()

  const headerRef = useRef<HTMLElement | null>(null)
  const sidebarRef = useRef<HTMLElement | null>(null)
  const accountMenuRef = useRef<HTMLDivElement | null>(null)
  const railFlyoutRef = useRef<HTMLDivElement | null>(null)
  // Set when a pop-out menu is opened from the keyboard, so focus moves into it.
  const focusFlyoutOnOpenRef = useRef(false)

  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const apply = () => {
      document.documentElement.style.setProperty('--app-header-height', `${el.offsetHeight}px`)
    }
    apply()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null
    ro?.observe(el)
    return () => {
      ro?.disconnect()
      document.documentElement.style.removeProperty('--app-header-height')
    }
  }, [])

  useEffect(() => {
    const navEl = sidebarRef.current
    const root = document.documentElement
    if (!navEl) return

    // Fires continuously while a phone's toolbar collapses during scroll: measure, then write only
    // real changes, at most once a frame (see utils/rootCssVars).
    const vars = createRootVarWriter(root)
    const recalculate = () => {
      vars.set('--app-bottom-nav-offset', bottomNavOffsetCss(navEl))
      // The part of the screen the keyboard leaves visible (iOS does not shrink 100dvh for it),
      // so dropdowns under the header can stop above the keyboard.
      vars.set('--app-visual-viewport-height', `${window.visualViewport?.height ?? window.innerHeight}px`)
    }
    const throttled = frameThrottle(recalculate)

    recalculate()

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          throttled.schedule()
        })
      : null

    resizeObserver?.observe(navEl)
    window.addEventListener('resize', throttled.schedule, { passive: true })
    window.visualViewport?.addEventListener('resize', throttled.schedule)
    window.visualViewport?.addEventListener('scroll', throttled.schedule)

    return () => {
      throttled.cancel()
      resizeObserver?.disconnect()
      window.removeEventListener('resize', throttled.schedule)
      window.visualViewport?.removeEventListener('resize', throttled.schedule)
      window.visualViewport?.removeEventListener('scroll', throttled.schedule)
      vars.remove('--app-bottom-nav-offset')
      vars.remove('--app-visual-viewport-height')
    }
  }, [])

  // The room the sidebar takes on the left, so ListPagerFooter sits beside it rather than under.
  useEffect(() => {
    const root = document.documentElement
    const width = isMobileViewport ? 0 : isSidebarPinned ? SIDEBAR_PINNED_WIDTH : SIDEBAR_RAIL_WIDTH
    root.style.setProperty('--app-sidebar-width', `${width}px`)
    return () => {
      root.style.removeProperty('--app-sidebar-width')
    }
  }, [isMobileViewport, isSidebarPinned])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const mobileMediaQuery = window.matchMedia('(max-width: 768px)')
    const updateViewport = () => {
      setIsMobileViewport(mobileMediaQuery.matches)
    }

    updateViewport()

    if (typeof mobileMediaQuery.addEventListener === 'function') {
      mobileMediaQuery.addEventListener('change', updateViewport)
      return () => {
        mobileMediaQuery.removeEventListener('change', updateViewport)
      }
    }

    mobileMediaQuery.addListener(updateViewport)
    return () => {
      mobileMediaQuery.removeListener(updateViewport)
    }
  }, [])

  // Account menu: close on outside click / Escape (mirrors NotificationBell behaviour)
  useEffect(() => {
    if (!isAccountMenuOpen) return undefined

    const handlePointerDown = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsAccountMenuOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isAccountMenuOpen])

  // Close the account menu and the rail's pop-outs whenever the route changes
  useEffect(() => {
    setIsAccountMenuOpen(false)
    setRailFlyout(null)
    setRailTip(null)
  }, [location.pathname, location.search])

  // Folded desktop rail: a group opens as a pop-out menu beside the rail. Pinned, it opens in
  // place (an accordion); on phones, as the sheet above the bottom bar.
  const isRailMode = !isMobileViewport && !isSidebarPinned

  const closeRailFlyout = useCallback(() => setRailFlyout(null), [])

  useEffect(() => {
    if (!isRailMode) {
      setRailFlyout(null)
      setRailTip(null)
    }
  }, [isRailMode])

  const toggleSidebarPinned = () => {
    setIsSidebarPinned((pinned) => {
      const next = !pinned
      try {
        window.localStorage.setItem(localStorageKeys.sidebarPinned, next ? '1' : '0')
      } catch {
        // Storage blocked: the choice lasts until reload.
      }
      return next
    })
  }

  const navigateFromSidebar = (path: string) => {
    navigate(path)
    setRailFlyout(null)
    setRailTip(null)
  }

  const centreOf = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect()
    return rect.top + rect.height / 2
  }

  /** On the rail, a mouse hover or keyboard focus shows the icon's name beside it (a tap does not). */
  const railTipHandlers = (label: string) => (isRailMode ? {
    onPointerEnter: (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerType === 'mouse') setRailTip({ label, top: centreOf(event.currentTarget) })
    },
    onPointerLeave: () => setRailTip(null),
    onFocus: (event: ReactFocusEvent<HTMLElement>) => {
      if (event.currentTarget.matches(':focus-visible')) setRailTip({ label, top: centreOf(event.currentTarget) })
    },
    onBlur: () => setRailTip(null),
  } : {})

  const handleGroupClick = (event: ReactMouseEvent<HTMLElement>, path: string, isOpen: boolean) => {
    if (!isRailMode) {
      setOpenDropdown(isOpen ? null : path)
      return
    }
    setRailTip(null)
    if (railFlyout?.path === path) {
      setRailFlyout(null)
      return
    }
    // Enter/Space (detail 0) moves focus into the menu; a click or tap leaves it where it is.
    focusFlyoutOnOpenRef.current = event.detail === 0
    setRailFlyout({ path, top: event.currentTarget.getBoundingClientRect().top })
  }

  // An open pop-out closes on a press anywhere outside it (its own icon toggles it instead),
  // a window resize or a scroll of the rail, since it is placed against the icon's position.
  useEffect(() => {
    if (!railFlyout) return undefined
    if (focusFlyoutOnOpenRef.current) {
      focusFlyoutOnOpenRef.current = false
      railFlyoutRef.current?.querySelector<HTMLElement>('button')?.focus()
    }
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null
      if (target && railFlyoutRef.current?.contains(target)) return
      if (target?.closest('[data-rail-group]')) return
      setRailFlyout(null)
    }
    const navEl = sidebarRef.current?.querySelector('nav')
    document.addEventListener('pointerdown', handlePointerDown)
    window.addEventListener('resize', closeRailFlyout)
    navEl?.addEventListener('scroll', closeRailFlyout)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      window.removeEventListener('resize', closeRailFlyout)
      navEl?.removeEventListener('scroll', closeRailFlyout)
    }
  }, [railFlyout, closeRailFlyout])

  // Escape closes the phone menu sheet, or the rail's pop-out (focus goes back to its icon).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (isMobileViewport) {
        setOpenDropdown(null)
        return
      }
      if (railFlyout) {
        const focusWasInside = !!railFlyoutRef.current?.contains(document.activeElement)
        setRailFlyout(null)
        if (focusWasInside) {
          sidebarRef.current?.querySelector<HTMLElement>(`[data-rail-group="${railFlyout.path}"]`)?.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isMobileViewport, railFlyout])

  const isSuperAdmin = user?.role === 'super_admin'
  const currentRole = user?.role || ''
  const tradeRequiredPlanLabel = tierName(requiredTierFor('tradeCenter'))
  const qrRequiredPlanLabel = tierName(requiredTierFor('qrCodes'))
  const bulkRequiredPlanLabel = tierName(requiredTierFor('bulkOps'))
  const calendarRequiredPlanLabel = tierName(requiredTierFor('calendar'))

  const settingsNavItem: NavItem = { path: '/settings', icon: Settings, label: 'Settings', exact: false }

  // Items shown in the account menu (sidebar footer). Logout is appended in the JSX.
  const accountMenuItems: NavItem[] = [
    { path: '/settings', icon: Settings, label: 'Settings' },
    { path: '/support', icon: LifeBuoy, label: 'Support' },
    { path: '/documentation', icon: BookOpen, label: 'Documentation' },
  ]

  // Fetch platform integrations to gate eBay Auctions nav item visibility
  useEffect(() => {
    if (!user) return
    let cancelled = false
    api.get('/account/overview').then((res) => {
      if (cancelled) return
      const platforms: Array<{ platform: string; isActive: boolean; isConnected?: boolean }> = res.data?.data?.platforms || []
      const ebayActive = platforms.some(
        (p) => p.platform === 'ebay' && p.isActive && (p.isConnected ?? true)
      )
      setHasEbayIntegration(ebayActive)
    }).catch(() => {
      // Non-critical — eBay nav defaults to hidden
    })
    return () => { cancelled = true }
  }, [user])

  const handleLogout = () => {
    setShowLogoutModal(true)
  }

  const confirmLogout = async () => {
    try {
      setIsLoggingOut(true)
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setIsLoggingOut(false)
      setShowLogoutModal(false)
    }
  }

  const isActive = (path: string, exact = false) => {
    const [normalizedPath, rawSearch] = (path || '').split('?')

    if (normalizedPath === '/dashboard') {
      /** Authenticated app shell mounts dashboard only at `/dashboard` (see `App.tsx` protected routes). Public `/` uses `PublicLayout`, not `MainLayout`. */
      return location.pathname === '/dashboard'
    }

    const pathMatches = exact
      ? location.pathname === normalizedPath
      : location.pathname.startsWith(normalizedPath)

    if (!rawSearch) {
      return pathMatches
    }

    if (!pathMatches) return false

    const requiredParams = new URLSearchParams(rawSearch)
    if ([...requiredParams.keys()].length === 0) return pathMatches

    const currentParams = new URLSearchParams(location.search)
    for (const [key, value] of requiredParams.entries()) {
      if (currentParams.get(key) !== value) {
        return false
      }
    }

    return true
  }

  // Super admin gets a streamlined navigation focused on management and debugging
  const adminNavItems: NavItem[] = [
    { path: '/dashboard', icon: Home, label: 'Admin Dashboard' },
    { path: '/super-admin/metrics', icon: BarChart3, label: 'Metrics' },
    { path: '/super-admin/organizations', icon: Shield, label: 'Organizations' },
    { path: '/super-admin/deal-codes', icon: Tag, label: 'Deal Codes' },
    { path: '/super-admin/database', icon: Database, label: 'Database' },
    { path: '/super-admin/plan-limits', icon: Gauge, label: 'Plan Limits' },
    { path: '/super-admin/review', icon: Shield, label: 'System Health' },
    { path: '/super-admin/tickets', icon: LifeBuoy, label: 'Support Tickets' },
    { path: '/super-admin/announcements', icon: Megaphone, label: 'Announcements' },
    { path: '/super-admin/test', icon: FlaskConical, label: 'Test Features' },
    { path: '/scanner', icon: ScanLine, label: 'Card Scanner (beta)' },
    { path: '/scanner/centering', icon: Ruler, label: 'Centering Check (beta)' },
    settingsNavItem,
  ]

  // Regular users: action items at top, utility items at bottom
  const canAccessSocialPlannerByRole = ['super_admin', 'owner', 'admin', 'manager'].includes(currentRole)
  // Show the Social Planner menu if user has the right role (manager+); calendar is tier-gated inside
  const canShowSocialPlannerMenu = ['super_admin', 'owner', 'admin', 'manager', 'staff'].includes(currentRole)

  // QR + Social Planner (+ eBay when connected) collapse into a single "Tools" group.
  // Shared by the desktop submenu and the mobile "More" overflow. QR always shows
  // (locked without the tier), so the group is never empty.
  const toolsSubmenu: NavSubItem[] = [
    { path: '/qr', icon: QrCode, label: 'QR Codes', locked: !canAccessQrCodes, requiredPlanLabel: qrRequiredPlanLabel },
    { path: '/tools/screensaver', icon: Maximize2, label: 'Store Display', locked: !can('storeDisplay'), requiredPlanLabel: tierName(requiredTierFor('storeDisplay')) },
    // Single scans on every plan; batch mode is locked inside for plans below Professional.
    { path: '/scanner', icon: ScanLine, label: 'Card Scanner (beta)' },
    { path: '/scanner/centering', icon: Ruler, label: 'Centering Check (beta)', locked: !can('centering'), requiredPlanLabel: tierName(requiredTierFor('centering')) },
    ...(canShowSocialPlannerMenu ? [
      {
        // Single Social Hub entry. Managers with social posts (Professional+) land on the Posts
        // home; everyone else lands on the Calendar tab (Basic+). The side-tab rail handles
        // switching between Calendar / Posts / Create Post from there.
        path: canAccessSocialPlannerByRole && can('socialPosts') ? '/social-planner' : '/social-planner/calendar',
        icon: Megaphone,
        label: 'Social Planner',
        roleRestricted: false,
        locked: !canAccessCalendarTier,
        requiredPlanLabel: calendarRequiredPlanLabel,
      },
    ] : []),
    ...(env.EBAY_ENABLED && hasEbayIntegration ? [{ path: '/auctions', icon: Gavel, label: 'eBay Auctions' }] : []),
  ]


  const mainNavItems: NavItem[] = [
    { path: '/dashboard', icon: Home, label: 'Home', exact: true },
    {
      path: '#transactions',
      icon: ArrowLeftRight,
      label: 'Transactions',
      submenu: [
        { path: '/transactions', icon: ArrowLeftRight, label: 'All Transactions', exact: true },
        { path: '/transactions/orders', icon: BarChart3, label: 'Orders (Online)' },
        { path: '/transactions/in-store', icon: ClipboardList, label: 'In-Store' },
        { path: '/transactions/create', icon: Plus, label: 'Create Transaction' },
      ],
    },
    {
      path: '#analytics',
      icon: BarChart3,
      label: 'Analytics',
      submenu: [
        { path: '/analytics', icon: Home, label: 'Overview', exact: true },
        { path: '/analytics/online', icon: BarChart3, label: 'Shopify & Online' },
        { path: '/analytics/in-store', icon: ArrowLeftRight, label: 'In-Store', locked: !canAccessTradeCenter, requiredPlanLabel: tradeRequiredPlanLabel },
        { path: '/analytics/inventory', icon: ClipboardList, label: 'Inventory' },
      ],
    },
    {
      path: '#inventory',
      icon: ClipboardList,
      label: 'Inventory',
      submenu: [
        { path: '/inventory/products', icon: ClipboardList, label: 'Products', exact: true },
        { path: '/inventory/tags', icon: Tag, label: 'Tags', exact: true },
        { path: '/inventory/add', icon: Plus, label: 'Add to Inventory' },
        { path: '/inventory/custom', icon: PackagePlus, label: 'Add Custom Product' },
        { path: '/inventory/csv', icon: FileSpreadsheet, label: 'Add by CSV', locked: !canBulkOps, requiredPlanLabel: bulkRequiredPlanLabel },
        { path: '/consignment', icon: Handshake, label: 'Consignment' },
      ]
    },
    { path: '#tools', icon: Wrench, label: 'Tools', submenu: toolsSubmenu },
  ]

  // Mobile "More" overflow mirrors the desktop Tools group. Documentation /
  // Support / Settings now live in the header account menu (visible on mobile too).
  const mobileOverflowNavItems: NavSubItem[] = toolsSubmenu

  const mobileNavItems: NavItem[] = isSuperAdmin
    ? [
        { path: '/dashboard', icon: Home, label: 'Dashboard' },
        { path: '/super-admin/metrics', icon: BarChart3, label: 'Metrics' },
        { path: '/super-admin/organizations', icon: Shield, label: 'Orgs' },
        { path: '/super-admin/database', icon: Database, label: 'DB' },
        {
          path: '#more',
          icon: Menu,
          label: 'More',
          submenu: adminNavItems
            .slice(4)
            .flatMap((item) => (item.submenu ? item.submenu : [item])),
        },
      ]
    : ([
        { path: '/dashboard', icon: Home, label: 'Home', exact: true },
        mainNavItems.find((item) => item.path === '#transactions'),
        mainNavItems.find((item) => item.path === '#analytics'),
        mainNavItems.find((item) => item.path === '#inventory'),
        {
          path: '#tools',
          icon: Wrench,
          label: 'Tools',
          submenu: mobileOverflowNavItems,
        },
      ].filter((item): item is NavItem => Boolean(item)))

  // The card search page is the global search with its own box; a second one in the header
  // only cost a row of a phone screen.
  const isCardSearchRoute = location.pathname === '/search/cards'
  const hideHeaderGlobalSearch = isMobileViewport && (location.pathname === '/inventory/add' || isCardSearchRoute)
  const [isHeaderSearchExpanded, setIsHeaderSearchExpanded] = useState(false)
  const closeHeaderSearch = useCallback(() => setIsHeaderSearchExpanded(false), [])

  useEffect(() => {
    if (!isMobileViewport && isHeaderSearchExpanded) {
      setIsHeaderSearchExpanded(false)
    }
  }, [isHeaderSearchExpanded, isMobileViewport])

  useEffect(() => {
    setIsHeaderSearchExpanded(false)
  }, [location.pathname, location.search])

  // Documentation / Support / Settings now live in the account menu (sidebar footer),
  // so the rail is purely navigation. Mobile keeps them under the "More" overflow.
  const allNavItems = isSuperAdmin ? adminNavItems : mainNavItems
  const primaryNavItems = isMobileViewport ? mobileNavItems : allNavItems
  const railFlyoutItem = railFlyout
    ? primaryNavItems.find((item) => item.path === railFlyout.path && item.submenu)
    : undefined

  /** A group's pages: the pinned accordion, the phone sheet and the rail pop-out all list them the same way. */
  const renderSubmenuItems = (item: NavItem) =>
    item.submenu?.map((subItem) => {
      const SubIcon = subItem.icon
      const isRestricted = !!subItem.roleRestricted

      if (isRestricted) {
        return (
          <div
            key={subItem.path}
            className={`${styles.submenuItem} ${styles.submenuItemDisabled}`}
            title={subItem.restrictedLabel || 'Access restricted'}
            aria-disabled="true"
          >
            <span className={styles.navIcon} aria-hidden="true">
              <SubIcon size={18} />
            </span>
            <span className={styles.navLabel}>
              {subItem.label}
              <Lock size={11} className={styles.navLock} />
              <span className={styles.navLockText}>{subItem.restrictedLabel || 'Restricted'}</span>
            </span>
          </div>
        )
      }

      return (
        <button
          key={subItem.path}
          type="button"
          onClick={() => {
            navigateFromSidebar(subItem.path)
            // On mobile, close the overlay immediately so it doesn't
            // block the page the user just navigated to.
            if (isMobileViewport) {
              setOpenDropdown(null)
            } else if (!isRailMode) {
              setOpenDropdown(item.path)
            }
          }}
          className={`${styles.submenuItem} ${isActive(subItem.path, !!subItem.exact) ? styles.active : ''}`}
          aria-current={isActive(subItem.path, !!subItem.exact) ? 'page' : undefined}
        >
          <span className={styles.navIcon} aria-hidden="true">
            <SubIcon size={18} />
          </span>
          <span className={styles.navLabel}>
            {subItem.label}
            {subItem.locked && <PlanLock plan={subItem.requiredPlanLabel} size={12} />}
          </span>
        </button>
      )
    })

  useEffect(() => {
    // On mobile the submenu is a fixed overlay — close it on every navigation
    // so it never blocks the page the user just navigated to.
    const isMobile = window.matchMedia('(max-width: 768px)').matches
    if (isMobile) {
      setOpenDropdown(null)
      return
    }

    // Desktop: auto-open the submenu whose child route is currently active
    const submenuGroups = allNavItems.filter((item) => Array.isArray(item.submenu))

    const activeSubmenu = submenuGroups.find((item) =>
      item.submenu?.some((sub) => isActive(sub.path, !!sub.exact))
    )

    setOpenDropdown(activeSubmenu?.path || null)
  }, [location.pathname, location.search, isSuperAdmin])

  return (
    <div className={`${styles.layout} ${isSidebarPinned ? styles.sidebarPinned : ''}`}>
      <RateLimitBanner />
      {/* Skip to main content link for keyboard navigation */}
      <a href="#main-content" className={styles.skipLink}>
        Skip to main content
      </a>
      
      {/* Phone menu sheet: dims the page, and a tap outside the sheet closes it instead of
          landing on whatever was underneath. The bottom bar stays above it to switch menus. */}
      {isMobileViewport && openDropdown && (
        <div className={styles.navScrim} onClick={() => setOpenDropdown(null)} aria-hidden="true" />
      )}

      {/* Sidebar Navigation */}
      <aside
        ref={sidebarRef}
        className={`${styles.sidebar} ${isSidebarPinned ? styles.pinned : ''}`}
        data-app-shell-nav="true"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className={styles.sidebarHeader}>
          <div className={styles.brandName}>
            <span className={styles.brandFull}>TCGEasy</span>
            <img src="/images/logo/tcgeasy-icon.png" alt="TCGEasy Logo" className={styles.brandMark} />
          </div>
          <p className={styles.tagline}>Smart Inventory for Smart Collectors</p>
        </div>

        {!isSuperAdmin && (
          <div className={styles.ctaGroup}>
            <button
              type="button"
              onClick={() => navigateFromSidebar('/transactions/create')}
              className={`${styles.createCta} ${isActive('/transactions/create', true) ? styles.createCtaActive : ''}`}
              aria-label="Create a new transaction"
              {...railTipHandlers('Add New Transaction')}
            >
              {/* Its own icon: in the collapsed rail the two buttons were identical red "+"s. */}
              <span className={styles.createCtaIcon} aria-hidden="true">
                <ArrowLeftRight size={18} />
              </span>
              <span className={styles.createCtaText}>Add New Transaction</span>
            </button>
            <button
              type="button"
              onClick={() => navigateFromSidebar('/inventory/add')}
              className={`${styles.createCta} ${isActive('/inventory/add', true) ? styles.createCtaActive : ''}`}
              aria-label="Add a new card"
              {...railTipHandlers('Add New Card')}
            >
              <span className={styles.createCtaIcon} aria-hidden="true">
                <Plus size={18} />
              </span>
              <span className={styles.createCtaText}>Add New Card</span>
            </button>
          </div>
        )}
        <nav className={styles.nav} aria-label="Primary navigation">
          {primaryNavItems.map((item) => {
            const IconComponent = item.icon
            const hasSubmenu = !!item.submenu
            const isDropdownOpen = isRailMode ? railFlyout?.path === item.path : openDropdown === item.path
            const isSubmenuActive = hasSubmenu && !!item.submenu?.some((sub) => isActive(sub.path, !!sub.exact))
            
            if (hasSubmenu) {
              return (
                <div key={item.path} className={styles.navItemWrapper}>
                  <button
                    onClick={(event) => handleGroupClick(event, item.path, isDropdownOpen)}
                    className={`${styles.navItem} ${isSubmenuActive || isDropdownOpen ? styles.active : ''}`}
                    aria-expanded={isDropdownOpen}
                    aria-label={`${item.label} menu`}
                    data-rail-group={item.path}
                    {...railTipHandlers(item.label)}
                  >
                    <span className={styles.navIcon} aria-hidden="true">
                      <IconComponent size={20} />
                    </span>
                    <span className={styles.navLabel}>{item.label}</span>
                    {item.locked && <PlanLock plan={item.requiredPlanLabel} size={13} />}
                    <span className={`${styles.chevron} ${isDropdownOpen ? styles.chevronOpen : ''}`}>
                      <ChevronRight size={16} />
                    </span>
                  </button>
                  {!isRailMode && isDropdownOpen && (
                    <div className={styles.submenu}>
                      {renderSubmenuItems(item)}
                    </div>
                  )}
                </div>
              )
            }
            
            return (
              <button
                key={item.path}
                onClick={() => navigateFromSidebar(item.path)}
                className={`${styles.navItem} ${isActive(item.path, !!item.exact) ? styles.active : ''}`}
                aria-current={isActive(item.path, !!item.exact) ? 'page' : undefined}
                aria-label={`Navigate to ${item.label}`}
                {...railTipHandlers(item.label)}
              >
                <span className={styles.navIcon} aria-hidden="true">
                  <IconComponent size={20} />
                </span>
                <span className={styles.navLabel}>
                  {item.label}
                  {item.path === '/support' && unreadTicketCount > 0 && (
                    <span className={styles.navBadge} aria-label={`${unreadTicketCount} unread`}>
                      ({unreadTicketCount})
                    </span>
                  )}
                  {item.locked && <PlanLock plan={item.requiredPlanLabel} size={13} />}
                </span>
              </button>
            )
          })}
        </nav>

        {/* Desktop only: pin the sidebar open (the page moves over) or fold it to the rail. */}
        <div className={styles.sidebarFooter}>
          <button
            type="button"
            onClick={toggleSidebarPinned}
            className={`${styles.navItem} ${styles.pinToggle}`}
            aria-label={isSidebarPinned ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-pressed={isSidebarPinned}
            {...railTipHandlers('Expand sidebar')}
          >
            <span className={styles.navIcon} aria-hidden="true">
              {isSidebarPinned ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
            </span>
            <span className={styles.navLabel}>Collapse</span>
          </button>
        </div>
      </aside>

      {/* Folded rail: a group's pop-out menu, beside the rail rather than over the page's width. */}
      {isRailMode && railFlyoutItem && (
        <div
          ref={railFlyoutRef}
          className={styles.railFlyout}
          style={{ top: railFlyout?.top }}
          aria-label={`${railFlyoutItem.label} menu`}
        >
          <div className={styles.railFlyoutTitle}>{railFlyoutItem.label}</div>
          {renderSubmenuItems(railFlyoutItem)}
        </div>
      )}
      {isRailMode && railTip && !railFlyout && (
        <div className={styles.railTip} style={{ top: railTip.top }} role="tooltip">
          {railTip.label}
        </div>
      )}

      {/* Main Content */}
      <main className={styles.main} role="main">
        {/* Top Header with Currency Selector */}
        <header className={styles.header} role="banner" ref={headerRef}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              {location.pathname !== '/dashboard' && location.pathname !== '/' && (
                <button 
                  onClick={() => navigate(-1)} 
                  className={styles.backBtn}
                  title="Go Back"
                  aria-label="Go back to previous page"
                >
                  <span className={styles.backIcon} aria-hidden="true">
                    <ArrowLeft size={18} />
                  </span>
                  <span className={styles.backText}>Back</span>
                </button>
              )}
            </div>
            <div className={styles.headerCenter}>
              {!isMobileViewport && !hideHeaderGlobalSearch ? <GlobalCardSearch /> : null}
            </div>
            <div className={styles.headerRight}>
              {isMobileViewport && !hideHeaderGlobalSearch && (
                <button
                  type="button"
                  className={styles.headerCalendarBtn}
                  onClick={() => setIsHeaderSearchExpanded(true)}
                  aria-label="Search all cards"
                  aria-haspopup="dialog"
                  title="Search"
                >
                  <Search size={20} />
                </button>
              )}
              <button
                type="button"
                className={styles.headerCalendarBtn}
                onClick={() => navigate('/scanner')}
                aria-label="Scan cards"
                title="Scan cards"
              >
                <ScanLine size={20} />
              </button>
              {!isSuperAdmin && (
                <button
                  type="button"
                  className={styles.headerCalendarBtn}
                  onClick={() => navigate('/transactions/create')}
                  aria-label="Trades"
                  title="Trades"
                >
                  <ArrowLeftRight size={20} />
                </button>
              )}
              {canShowSocialPlannerMenu && (
                <button
                  type="button"
                  className={styles.headerCalendarBtn}
                  onClick={() => navigate('/social-planner/calendar')}
                  aria-label="Events calendar"
                  title="Events calendar"
                >
                  <CalendarDays size={20} />
                </button>
              )}
              <NotificationBell />
              {/* Account menu: identity, appearance, currency, utility links, logout */}
              <div className={styles.accountMenuWrapper} ref={accountMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsAccountMenuOpen((open) => !open)}
                  className={`${styles.accountTrigger} ${isAccountMenuOpen ? styles.accountTriggerOpen : ''}`}
                  aria-haspopup="menu"
                  aria-expanded={isAccountMenuOpen}
                  aria-label="Account menu"
                >
                  <span className={styles.userAvatar} aria-hidden="true">
                    {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </span>
                  <span className={styles.accountTriggerName}>
                    {user?.firstName || user?.email}
                  </span>
                  <span className={`${styles.userChevron} ${isAccountMenuOpen ? styles.userChevronOpen : ''}`} aria-hidden="true">
                    <ChevronDown size={16} />
                  </span>
                </button>

                {isAccountMenuOpen && (
                  <div className={styles.accountMenu} role="menu" aria-label="Account menu">
                    <div className={styles.accountIdentity}>
                      <span className={styles.userAvatar} aria-hidden="true">
                        {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </span>
                      <div className={styles.userDetails}>
                        <div className={styles.userName}>
                          {user?.firstName} {user?.lastName}
                        </div>
                        <div className={styles.userEmail}>{user?.email}</div>
                      </div>
                    </div>

                    <div className={styles.accountMenuDivider} />

                    {/* Appearance toggle */}
                    <button
                      role="menuitem"
                      type="button"
                      onClick={toggleTheme}
                      className={styles.accountMenuItem}
                      aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                      <span className={styles.accountMenuIcon} aria-hidden="true">
                        {theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />}
                      </span>
                      <span className={styles.accountMenuLabel}>
                        {theme === 'dark' ? 'Dark mode' : 'Light mode'}
                      </span>
                      <span className={styles.accountMenuHint}>
                        {theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
                      </span>
                    </button>

                    <div className={styles.accountMenuDivider} />

                    {/* Currency picker */}
                    <div className={styles.accountMenuSectionLabel}>Currency</div>
                    <div className={styles.currencyOptions}>
                      {CURRENCIES.map((curr) => (
                        <button
                          key={curr.code}
                          role="menuitemradio"
                          aria-checked={curr.code === currency}
                          type="button"
                          onClick={() => setCurrency(curr.code)}
                          className={`${styles.currencyChip} ${curr.code === currency ? styles.currencyChipActive : ''}`}
                          title={`${curr.name} — 1 USD = ${curr.rate} ${curr.code}`}
                        >
                          <span className={styles.currencyChipFlag} aria-hidden="true">{curr.flag}</span>
                          <span className={styles.currencyChipCode}>{curr.code}</span>
                          {curr.code === currency && (
                            <Check size={14} className={styles.currencyChipCheck} aria-hidden="true" />
                          )}
                        </button>
                      ))}
                    </div>

                    <div className={styles.accountMenuDivider} />

                    {accountMenuItems.map((item) => {
                      const IconComponent = item.icon
                      return (
                        <button
                          key={item.path}
                          role="menuitem"
                          type="button"
                          onClick={() => {
                            setIsAccountMenuOpen(false)
                            navigate(item.path)
                          }}
                          className={`${styles.accountMenuItem} ${isActive(item.path) ? styles.active : ''}`}
                          aria-current={isActive(item.path) ? 'page' : undefined}
                        >
                          <span className={styles.accountMenuIcon} aria-hidden="true">
                            <IconComponent size={18} />
                          </span>
                          <span className={styles.accountMenuLabel}>
                            {item.label}
                            {item.path === '/support' && unreadTicketCount > 0 && (
                              <span className={styles.navBadge} aria-label={`${unreadTicketCount} unread`}>
                                ({unreadTicketCount})
                              </span>
                            )}
                          </span>
                        </button>
                      )
                    })}

                    <div className={styles.accountMenuDivider} />

                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => {
                        setIsAccountMenuOpen(false)
                        handleLogout()
                      }}
                      className={`${styles.accountMenuItem} ${styles.accountMenuLogout}`}
                      aria-label={`Logout ${user?.firstName || user?.email || 'user'}`}
                    >
                      <span className={styles.accountMenuIcon} aria-hidden="true">
                        <LogOut size={18} />
                      </span>
                      <span className={styles.accountMenuLabel}>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        {isMobileViewport && isHeaderSearchExpanded && <GlobalSearchSheet onClose={closeHeaderSearch} />}

        {overLimitWarning && (
          <div className={styles.warningBanner} role="alert">
            <div className={styles.warningContent}>
              <span>⚠️ {overLimitWarning}</span>
              <button onClick={() => navigate('/pricing?source=over-limit')} className={styles.verifyBtn}>
                View plans
              </button>
            </div>
          </div>
        )}

        {/* Email Verification Warning Banner */}
        {authWarning && authWarning.emailNotVerified && (
          <div className={styles.warningBanner} role="alert">
            <div className={styles.warningContent}>
              <span>⚠️ {authWarning.message}</span>
              <button 
                onClick={() => navigate('/verify-email')}
                className={styles.verifyBtn}
              >
                Resend Verification
              </button>
              <button 
                onClick={dismissWarning}
                className={styles.dismissBtn}
                aria-label="Dismiss warning"
              >
                ×
              </button>
            </div>
          </div>
        )}

        <div id="main-content" className={styles.content} tabIndex={-1}>
          {/* A page crash replaces only the page, so the nav is still there to leave it.
              The app-wide boundary in App.tsx still catches anything outside this. */}
          <ErrorBoundary
            resetKey={location.pathname}
            fallback={
              <div className={styles.pageError} role="alert">
                <h2 className={styles.pageErrorTitle}>This page hit a problem</h2>
                <p className={styles.pageErrorText}>
                  Reload to try again, or pick another page from the menu.
                </p>
                <button type="button" className={styles.pageErrorBtn} onClick={() => window.location.reload()}>
                  Reload page
                </button>
              </div>
            }
          >
            {children}
          </ErrorBoundary>
        </div>

      </main>

      <ActionConfirmModal
        isOpen={showLogoutModal}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmLabel="Logout"
        cancelLabel="Cancel"
        tone="danger"
        isLoading={isLoggingOut}
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutModal(false)}
      />
    </div>
  )
}

export default MainLayout
